import { useEffect, useState } from "react";
import { http, formatApiError, askDelete, delTitle } from "../lib/api";
import { useRealtime } from "../context/RealtimeContext";
import { ClipboardList, Download, Trash2, X, Building2, User, Filter } from "lucide-react";
import { REPORT_TYPES, reportType } from "./AgentReports";
import { ExportPdfButton, TodayButton, todayISO, periodLabel } from "../components/ExportPdfButton";
import { DateInput } from "../components/DateInput";
import { SearchSelect } from "../components/SearchSelect";

const fmtDT = (iso) => new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

export default function Reports() {
  const [items, setItems] = useState([]);
  const [agents, setAgents] = useState([]);
  const [sites, setSites] = useState([]);
  const [f, setF] = useState({ agent_id: "", site_id: "", type: "", start: todayISO(), end: todayISO() });
  const [open, setOpen] = useState(null);
  const { lastEvent } = useRealtime();

  const load = async () => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    const r = await http.get("/reports", { params });
    setItems(r.data);
  };
  useEffect(() => {
    http.get("/agents").then((r) => setAgents(r.data));
    http.get("/sites").then((r) => setSites(r.data));
  }, []);
  useEffect(() => { load(); }, [f]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (lastEvent?.event === "report_changed") load(); }, [lastEvent]); // eslint-disable-line react-hooks/exhaustive-deps

  const openReport = async (r) => {
    setOpen(r);
    if (!r.read_at) { await http.post(`/reports/${r.id}/read`); load(); }
  };
  const unread = items.filter((r) => !r.read_at).length;
  const sel = "text-sm border border-slate-300 rounded-md px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-sky-500";

  return (
    <div className="p-8 max-w-[1800px] mx-auto" data-testid="reports-page">
      <div className="mb-6 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight">Rapports agents</h1>
          <p className="text-sm text-slate-600">{periodLabel(f.start, f.end)} · {items.length} rapport(s){unread ? ` · ${unread} non lu(s)` : ""}</p>
        </div>
        <ExportPdfButton testid="reports-export-pdf" title="Rapports agents" subtitle={periodLabel(f.start, f.end)} filename={`Rapports_${f.start || "tous"}`}
          columns={["Date", "Type", "Titre", "Agent", "Site", "Contenu"]}
          rows={() => items.map((r) => [fmtDT(r.created_at), reportType(r.type).label, r.title, r.agent_name, r.site_name || "—", r.body])} />
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="reports-filters">
        <Filter className="h-4 w-4 text-slate-400" />
        <TodayButton testid="reports-today" active={f.start === todayISO() && f.end === todayISO()} onClick={() => setF({ ...f, start: todayISO(), end: todayISO() })} />
        <select data-testid="reports-filter-agent" className={sel} value={f.agent_id} onChange={(e) => setF({ ...f, agent_id: e.target.value })}>
          <option value="">Tous les agents</option>
          {agents.map((a) => <option key={a.id} value={a.id}>{a.last_name} {a.first_name}{a.archived ? " (archivé)" : ""}</option>)}
        </select>
        <SearchSelect data-testid="reports-filter-site" className={sel} value={f.site_id} onChange={(e) => setF({ ...f, site_id: e.target.value })}>
          <option value="">Tous les sites</option>
          {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </SearchSelect>
        <select data-testid="reports-filter-type" className={sel} value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>
          <option value="">Tous les types</option>
          {REPORT_TYPES.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
        </select>
        <DateInput data-testid="reports-filter-start" className={sel} value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} />
        <span className="text-slate-400 text-sm">→</span>
        <DateInput data-testid="reports-filter-end" className={sel} value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} />
        {Object.values(f).some(Boolean) && <button data-testid="reports-filter-reset" onClick={() => setF({ agent_id: "", site_id: "", type: "", start: todayISO(), end: todayISO() })} className="text-xs font-semibold text-slate-600 hover:text-rose-600">Réinitialiser</button>}
      </div>

      <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-[10px] uppercase tracking-widest font-bold text-slate-500">
              <th className="px-4 py-3">Date</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Titre</th><th className="px-4 py-3">Agent</th><th className="px-4 py-3">Site</th><th className="px-4 py-3">Photos</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((r) => {
              const t = reportType(r.type);
              return (
                <tr key={r.id} data-testid={`report-row-${r.id}`} onClick={() => openReport(r)} className={`cursor-pointer hover:bg-sky-50/60 ${!r.read_at ? "bg-amber-50/40" : ""}`}>
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-600">{fmtDT(r.created_at)}</td>
                  <td className="px-4 py-3"><span className={`border rounded text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 ${t.cls}`}>{t.label}</span></td>
                  <td className="px-4 py-3 font-semibold">
                    {r.title}
                    {!r.read_at && <span data-testid={`report-new-${r.id}`} className="ml-2 rounded-full bg-rose-600 text-white text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5">Nouveau</span>}
                  </td>
                  <td className="px-4 py-3">{r.agent_name}</td>
                  <td className="px-4 py-3">{r.site_name || "—"}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{r.photo_file_ids?.length || "—"}</td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={6} className="text-center text-slate-500 py-10 text-sm"><ClipboardList className="h-6 w-6 mx-auto mb-2 text-slate-300" />Aucun rapport sur la période.</td></tr>}
          </tbody>
        </table>
      </div>
      {open && <ReportDetail r={open} onClose={() => setOpen(null)} onDeleted={() => { setOpen(null); load(); }} />}
    </div>
  );
}

function AuthImage({ fid }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let u;
    http.get(`/files/${fid}/download`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setSrc(u); }).catch(() => {});
    return () => u && URL.revokeObjectURL(u);
  }, [fid]);
  if (!src) return <div className="h-40 w-full rounded-md bg-slate-100 animate-pulse" />;
  return <a href={src} target="_blank" rel="noreferrer"><img src={src} alt="" className="h-40 w-full rounded-md object-contain bg-white border border-slate-200 hover:opacity-90" /></a>;
}

function ReportDetail({ r, onClose, onDeleted }) {
  const [err, setErr] = useState("");
  const t = reportType(r.type);
  const downloadPdf = async () => {
    try {
      const res = await http.get(`/reports/${r.id}/export.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      Object.assign(document.createElement("a"), { href: url, download: `Rapport_${t.label}_${r.created_at.slice(0, 10)}.pdf` }).click();
    } catch (e) { setErr(formatApiError(e)); }
  };
  const del = async () => { if (askDelete("Supprimer définitivement ce rapport ?")) { await http.delete(`/reports/${r.id}`); onDeleted(); } };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex justify-end">
      <div className="bg-white w-full max-w-xl h-full overflow-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="report-detail">
        <div className="flex items-start justify-between px-6 py-5 border-b border-slate-200 border-l-4" style={{ borderLeftColor: r.type === "incident" ? "#dc2626" : "var(--brand-primary)" }}>
          <div>
            <span className={`border rounded text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 ${t.cls}`}>{t.label}</span>
            <h2 className="font-display text-xl font-extrabold mt-2">{r.title}</h2>
            <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-3 gap-y-1">
              <span><User className="h-3 w-3 inline -mt-0.5" /> {r.agent_name}</span>
              {r.site_name && <span><Building2 className="h-3 w-3 inline -mt-0.5" /> {r.site_name}</span>}
              <span>{fmtDT(r.created_at)}</span>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-6 py-5 text-sm text-slate-800 whitespace-pre-line leading-relaxed" data-testid="report-detail-body">{r.body}</div>
        {r.photo_file_ids?.length > 0 && <div className="px-6 pb-5 grid grid-cols-2 gap-2">{r.photo_file_ids.map((fid) => <AuthImage key={fid} fid={fid} />)}</div>}
        {err && <div className="mx-6 text-sm text-rose-600">{err}</div>}
        <div className="px-6 py-4 border-t border-slate-200 flex gap-2">
          <button data-testid="report-pdf" onClick={downloadPdf} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-slate-900 hover:bg-slate-800 text-white rounded-md"><Download className="h-4 w-4" /> Export PDF</button>
          <button data-testid="report-delete" onClick={del} className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-rose-600 hover:bg-rose-50 rounded-md"><Trash2 className="h-4 w-4" /> {delTitle()}</button>
        </div>
      </div>
    </div>
  );
}
